import React from 'react';
import BugReportIcon from '@mui/icons-material/BugReport';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button } from '@rentspree/component-2023.components.atoms.button';
import { Chip } from '@rentspree/component-2023.components.atoms.chip';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';
import { Card } from '@rentspree/component-2023.components.organisms.card';

import { ErrorFallbackProps } from './ErrorBoundary';

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  resetError,
}) => {
  const isDevelopment = process.env.NODE_ENV === 'development';

  const handleReportError = () => {
    // In a real application, this would send the error to your error reporting service
    // eslint-disable-next-line no-console
    console.log('Error reported:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });

    // You could also copy error details to clipboard
    if (navigator.clipboard) {
      const errorDetails = `Error: ${error.message}\n\nStack: ${error.stack}\n\nComponent Stack: ${errorInfo.componentStack}`;
      navigator.clipboard.writeText(errorDetails);
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        padding: '20px',
      }}
    >
      <Card
        style={{
          maxWidth: '600px',
          width: '100%',
          padding: '32px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '24px',
          }}
        >
          <ErrorOutlineIcon
            style={{
              fontSize: '64px',
              color: '#ef4444',
            }}
          />
        </div>

        <Typography
          variant='h4'
          style={{
            marginBottom: '16px',
            color: '#111827',
            fontWeight: 700,
          }}
        >
          Something went wrong
        </Typography>

        <Typography
          variant='body1'
          style={{
            marginBottom: '24px',
            color: '#6b7280',
            lineHeight: '1.6',
          }}
        >
          We're sorry, but something unexpected happened. The error has been
          logged and we're working to fix it. Please try refreshing the page or
          contact support if the problem persists.
        </Typography>

        {isDevelopment && (
          <div style={{ marginBottom: '24px' }}>
            <Chip
              color='yellow'
              label={
                <Typography variant='caption' style={{ fontWeight: 500 }}>
                  Development Mode
                </Typography>
              }
              size='small'
              style={{ marginBottom: '16px' }}
            />

            <Card
              style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'left',
                marginBottom: '16px',
              }}
            >
              <Typography
                variant='subtitle2'
                style={{
                  color: '#92400e',
                  fontWeight: 600,
                  marginBottom: '8px',
                }}
              >
                Error Details:
              </Typography>
              <Typography
                variant='body2'
                style={{
                  color: '#92400e',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  lineHeight: '1.4',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {error.message}
              </Typography>
            </Card>

            {error.stack && (
              <Card
                style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fca5a5',
                  borderRadius: '8px',
                  padding: '16px',
                  textAlign: 'left',
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant='subtitle2'
                  style={{
                    color: '#dc2626',
                    fontWeight: 600,
                    marginBottom: '8px',
                  }}
                >
                  Stack Trace:
                </Typography>
                <Typography
                  variant='body2'
                  style={{
                    color: '#dc2626',
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    lineHeight: '1.4',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {error.stack}
                </Typography>
              </Card>
            )}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant='contained'
            color='primary'
            onClick={resetError}
            startIcon={<RefreshIcon />}
            size='large'
          >
            Try Again
          </Button>

          <Button
            variant='outlined'
            color='secondary'
            onClick={handleReload}
            size='large'
          >
            Reload Page
          </Button>

          {isDevelopment && (
            <Button
              variant='outlined'
              color='secondary'
              onClick={handleReportError}
              startIcon={<BugReportIcon />}
              size='large'
            >
              Copy Error Details
            </Button>
          )}
        </div>

        <Typography
          variant='caption'
          style={{
            marginTop: '24px',
            color: '#9ca3af',
            display: 'block',
          }}
        >
          Error ID: {Date.now().toString(36)}
        </Typography>
      </Card>
    </div>
  );
};
